
import moment from 'moment';
import { List_Tarifs_Course } from "./List_Tarifs_Course.class";
import { List_Vagues_Course } from "./List_Vagues_Course.class";
import TeamPricingService from "@/services/booking/teamPricing.service";

/**
 * @classdesc Une course
 * @kind class
 * @export
 * @prop {int} id - Id de la course
 * @prop {string} nom - Nom de la course
 * @prop {string} type - Type de course
 * @prop {int} id_module - Id du module
 * @prop {boolean} ouvert - Statut des inscriptions
 * @prop {date} date_depart - Date départ de la course
 * @prop {date} heure_depart - Heure départ de la course
 * @prop {int} max_inscrits - Limite d'inscriptions
 * @prop {int} nb_inscrits - Nombre d'inscriptions
 * @prop {int} nb_reservations - Nombre d'inscriptions + réservations en cours
 * @prop {array} metadatas - Liste des métadonnées
 * @prop {array} tarifs - Tarifs de la course
 * @prop {array} vagues - Vagues de la course
 * @prop {int} vague_selected - Vague sélectionnée
 * @class Course
 */
export default class Course {
    id = "";
    nom = "";
    id_module = "";
    ouvert = "0";
    date_depart = moment().format("YYYY-MM-DD");
    heure_depart = moment().format("HH:mm");
    max_inscrits = "";
    nb_inscrits = 0;
    nb_reservations = 0;
    nb_inscrits_valide = 0;
    nb_pendings_in_cart = 0;
    nb_pre_inscrits = 0;
    type = "solo";
    metadatas = [];
    tarifs = [];
    vagues = [];
    selected = false;
    vague_selected = 0;
    cart = { nb_articles: 0, prix: 0, prix_regulier: 0 };

    /**
     * Creates an instance of Course.
     * @constructs
     * @kind function
     * @memberof Course
     */
    constructor() { }


    /**
     * Défini la course à partir d'un jeu de données
     *
     * @param {Object} data
     * @memberof Course
     */
    set(data) {
        for (const [key, value] of Object.entries(data)) {
            if (this.hasOwnProperty(key)) {
                if (key == "tarifs") {
                    this.tarifs = new List_Tarifs_Course();
                    this.tarifs.set(value);
                }
                else if (key == "vagues") {
                    this.vagues = new List_Vagues_Course();
                    this.vagues.set(value);
                }
                else if (key == "cart") {
                    this.cart = {
                        nb_articles: 0,
                        prix: 0,
                        prix_regulier: 0,
                        ...(value || {}),
                        prix: parseFloat(value?.prix ?? 0) || 0,
                        prix_regulier: parseFloat(value?.prix_regulier ?? value?.prix ?? 0) || 0,
                        nb_articles: parseInt(value?.nb_articles ?? 0) || 0,
                    };
                }
                else {
                    this[key] = value;
                }
            }
            if (key == 'nb_enfants') { // For NDR24
                this.nb_enfants = value;
            }
        }
    }

    refresh_tarifs(refreshed_tarifs) {
        for (let i = 0; i < this.tarifs.list.length; i++) {
            const current_refresh_tarif = refreshed_tarifs[this.tarifs.list[i].id] ? refreshed_tarifs[this.tarifs.list[i].id] : null;
            if (current_refresh_tarif) {
                this.tarifs.list[i].refresh(current_refresh_tarif);
            }
        }
    }




    /* 
        Met à jour le prix de la course en fonction des tarifs et du coureur
    */
    update_cart(list_courses, packs, coureur, vip_ins = null, inscription = null, lien_groupe = null, pricingContext = null) {

        let cart = { nb_articles: 0, prix: 0, prix_regulier: 0 };

        if (this.selected && this.not_in_packs(packs)) {
            cart.nb_articles = 1;
            let right_tarif = this.tarifs.get_current_price_for_inscription(coureur, list_courses, inscription, this.nom);
            cart.prix = parseFloat(right_tarif[0]);
            cart.prix_regulier = parseFloat(right_tarif[0]);
            cart.id_tarif = parseInt(right_tarif[1]);
            if (vip_ins) {
                cart.prix = this.get_price_with_vip_link(vip_ins, cart.prix);
            }
            if (lien_groupe && !lien_groupe.doit_payer) {
                cart.prix = 0;
            }
            if (pricingContext) {
                const tarif = this.tarifs?.list?.find((t) => t.id == cart.id_tarif);
                if (this._tarifIsTeamPrice(tarif) && pricingContext.is_team_member && !pricingContext.is_leader) {
                    cart.prix = 0;
                    cart.prix_regulier = 0;
                    cart.is_team_price = true;
                } else if (this._tarifIsTeamPrice(tarif)) {
                    cart.is_team_price = true;
                    if (pricingContext.is_leader) {
                        TeamPricingService.applyTeamFFALicenceDiscount(cart, this, pricingContext);
                    }
                }
            }
            this.current_price = cart.prix;
        }
        this.cart = cart;

        return this.cart;
    }

    /*
        Vérifie si le tarif s'applique à l'équipe entière
    */
    _tarifIsTeamPrice(tarif) {
        if (!tarif) return false;
        if (typeof tarif.is_team_price === 'function') {
            return tarif.is_team_price();
        }
        const metadata = tarif.metadatas?.find?.((m) => m.nom == 'tarif_apply_equipe');
        return metadata?.valeur == '1';
    }

    /* 
        Récupère le prix de la course avec le lien VIP
    */
    get_price_with_vip_link(vip_ins, prix) {
        if (vip_ins.doit_payer_course == '1') {
            return prix;
        }
        if (vip_ins.limit_acces_parcours == '0') {
            return 0;
        }
        for (let i = 0; i < vip_ins.filter_parcours.length; i++) {
            if (vip_ins.filter_parcours[i].id == this.id) {
                return 0;
            }
        }
    }


    /* 
        Vérifie si la course n'est pas présente dans un pack
    */
    not_in_packs(packs) {
        for (let i = 0; i < packs.list.length; i++) {
            if (packs.list[i].selected && packs.list[i].course_in_pack(this.id)) {
                return false;
            }
        }
        return true;
    }

    is_in_selected_pack(packs) {
        for (let i = 0; i < packs.list.length; i++) {
            if (packs.list[i].selected && packs.list[i].course_in_pack(this.id)) {
                return true;
            }
        }
        return false;
    }

    /* 
        Vérifie si la course est complète (max_inscrits atteint par les inscriptions + reservations en cours)
    */
    is_complet(list_courses) {
        if (parseInt(this.max_inscrits) == 0) {
            return false;
        }
        return this.get_places_restantes(list_courses) <= 0;
    }


    /* 
        Vérifie si la course est une course en équipe (duo, equipe ou relais)
    */
    is_equipe_course() {
        if (this.type == 'duo' || this.type == 'equipe' || this.type == 'relais') {
            return true;
        }
    }

    /* 
        Récupère le nombre de coureurs par équipe (duo = 2, equipe = nb_coureurs_equipe, relais = nb_coureurs_equipe)
    */
    get_equipe_size() {
        if (this.type == 'duo') {
            return 2;
        }
        else if (this.type == 'equipe' || this.type == 'relais') {
            if (this.metadatas.nb_coureurs_equipe) {
                return parseInt(this.metadatas.nb_coureurs_equipe);
            }
        }
        return 1;
    }

    has_variable_equipe_size() {
        return this.metadatas?.nb_variable_coureur_equipe == '1';
    }

    get_equipe_size_min() {
        if (this.type == 'duo') {
            return 2;
        }
        if (this.has_variable_equipe_size()) {
            return parseInt(this.metadatas.nb_min_coureurs_equipe) || 1;
        }
        return this.get_equipe_size();
    }

    get_equipe_size_max() {
        if (this.type == 'duo') {
            return 2;
        }
        if (this.has_variable_equipe_size()) {
            return parseInt(this.metadatas.nb_max_coureurs_equipe) || this.get_equipe_size_min();
        }
        return this.get_equipe_size();
    }

    /* 
        Récupère le nombre de places restantes
    */


    get_places_restantes(list_courses) {

        if (this.metadatas.groupe_max && this.metadatas.groupe_max != "") {

            let reservation_in_group = this.get_reservations_in_group(list_courses, this.metadatas.groupe_max);
            let restantes = parseInt(this.max_inscrits) - parseInt(reservation_in_group);


            return Math.max(restantes, 0);
        }
        else {
            let restantes = parseInt(this.max_inscrits) - parseInt(this.nb_reservations) - parseInt(this.nb_pendings_in_cart);
            return Math.max(restantes, 0);
        }
    }

    get_reservations_in_group(list_courses, groupe_max) {
        let reservations = 0;
        if (!list_courses || !list_courses.list) {
            return 0;
        }


        for (let i = 0; i < list_courses.list.length; i++) {
            if (list_courses.list[i].metadatas.groupe_max == groupe_max) {
                reservations += parseInt(list_courses.list[i].nb_reservations);
                reservations += parseInt(list_courses.list[i].nb_pendings_in_cart);
            }
        }
        return reservations;
    }
    /* 
        Vérifie si la course est ouverte aux inscriptions (ouvert = 1, course non complète)
    */
    is_open() {

        return this.ouvert == '1' && !this.is_complet();
    }

    /* 
        Vérifie si la course est sélectionnable
    */
    is_selectionnable(statut_ouverture_module, admin_view, admin_ins, vip_ins, list_courses, module_complet, lien_groupe = null) {
        if (lien_groupe && lien_groupe.is_lien_groupe && (statut_ouverture_module == 'ouverte' || (statut_ouverture_module == 'avant_ouverture' && lien_groupe.allow_before_opening))) {

            if (lien_groupe.limit_acces_parcours) {
                const ids = lien_groupe.filter_parcours ? lien_groupe.filter_parcours.split(',').map(id => id.trim()) : [];
                const in_filter = ids.includes(String(this.id));
                if (!in_filter) return false;
            }
            const is_complet = this.is_complet(list_courses);
            const inscription_course_complete = lien_groupe.inscription_course_complete;
            if (inscription_course_complete) {
                return true;
            }
            const is_open = this.ouvert == '1';
            if (!is_open) {
                return false;
            }
            return !is_complet;
        }


        if (this.ouvert == '1' && statut_ouverture_module == 'ouverte' && !this.is_complet(list_courses) && !module_complet) {
            return true;
        }
        if (admin_view && !this.is_complet(list_courses) && !module_complet) {
            return true;
        }
        if (admin_ins) {
            return true;
        }

        if (vip_ins && statut_ouverture_module == 'ouverte') {
            if (vip_ins.limit_acces_parcours == '0') {
                return true;
            }
            else {
                for (let i = 0; i < vip_ins.filter_parcours.length; i++) {
                    if (vip_ins.filter_parcours[i].id == this.id) {
                        return true;
                    }
                }
            }

        }
        return false;
    }

    /* 
    Vérifie si le max inscrit est modifiable par l'organisateur (si la course n'est pas dans un groupe)    
    */
    is_max_editable() {
        if (this.metadatas.groupe_max && this.metadatas.groupe_max != "") {
            return false;
        }
        return true;
    }

    is_solo() {
        return this.type == "solo" || this.type == "rando";
    }
    has_max_inscrits() {
        return parseInt(this.max_inscrits) > 0;
    }

    /* ------------------------------------ */

    has_vagues() {
        return this.vagues.list.length > 0;
    }

    get_selected_vague() {
        if (!this.vague_selected || !this.vagues?.list?.length) {
            return null;
        }
        return this.vagues.list.find(vague => vague.id == this.vague_selected) ?? null;
    }

    getSimplifiedData() {
        if (!this.selected) return null;
        const infos = {};
        infos.id = parseInt(this.id);
        infos.id_tarif = parseInt(this?.cart?.id_tarif);
        infos.id_vague = parseInt(this.vague_selected);
        infos.nom = this.nom;
        infos.prix = this?.cart?.prix;

        return infos;
    }

    display_depart_on_home() {
        return this.metadatas?.discipline != 'Non sportif' && this.date_depart != '0000-00-00';
    }

    has_preinscriptions() {
        return this.metadatas?.has_preinscriptions == '1';
    }

    has_postal_send() {
        return this.metadatas?.has_postal_send == '1';
    }

    get_readable_date_depart() {
        let date_depart = moment(this.date_depart).format("dddd DD MMMM");
        date_depart = date_depart.charAt(0).toUpperCase() + date_depart.slice(1);
        return date_depart;
    }

    get_readable_heure_depart() {
        return this.heure_depart != '00:00:00' ? 'à ' + this.heure_depart : '';
    }

    has_metadata(name) {
        return this.metadatas?.hasOwnProperty(name);
    }

    get_metadata(name) {
        return this.metadatas?.hasOwnProperty(name) ? this.metadatas[name] : false;
    }

    hide_vagues_from_user() {
        return this.metadatas?.hide_vagues_from_user == '1';
    }


}
